import { NativeModules, Platform, Vibration } from 'react-native';
import { retrieveSetting, storeSetting } from '../Common/common';

export const SOUND_ALERT_SETTING_KEY = 'auth_sound_alert_enabled';

/**
 * Checks whether sound alert is enabled by user.
 * Defaults to true if not explicitly set to 'false'.
 */
export const isSoundAlertEnabled = async (): Promise<boolean> => {
  try {
    const val = await retrieveSetting(SOUND_ALERT_SETTING_KEY);
    return val === null || val === undefined || val === 'true';
  } catch {
    return true;
  }
};

/**
 * Updates the sound alert setting.
 */
export const setSoundAlertEnabled = async (enabled: boolean): Promise<void> => {
  try {
    await storeSetting(SOUND_ALERT_SETTING_KEY, enabled ? 'true' : 'false');
  } catch (error) {
    console.error('Failed to save sound alert setting:', error);
  }
};

/**
 * Plays a pleasant three-tone harmonic chime using Web Audio API on web browsers.
 */
export const playWebChime = (): void => {
  try {
    if (typeof window === 'undefined') return;
    const AudioContextClass =
      window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;

    const ctx = new AudioContextClass();
    const playNote = (freq: number, start: number, dur: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
      gain.gain.setValueAtTime(0.2, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur);
    };

    // Musical chord arpeggio: C5 (523.25Hz), E5 (659.25Hz), G5 (783.99Hz)
    playNote(523.25, 0, 0.15);
    playNote(659.25, 0.12, 0.15);
    playNote(783.99, 0.24, 0.35);
  } catch (e) {
    console.warn('Web chime error:', e);
  }
};

/**
 * Plays the sound alert for authorization requests or software notifications.
 * Respects user preference unless `force` is set to true (used for preview/testing).
 */
export const playSoundAlert = async (force: boolean = false): Promise<boolean> => {
  try {
    if (!force) {
      const enabled = await isSoundAlertEnabled();
      if (!enabled) return false;
    }

    if (Platform.OS === 'android') {
      const soundAlertModule = NativeModules?.SoundAlert;
      if (soundAlertModule && typeof soundAlertModule.playNotificationSound === 'function') {
        soundAlertModule.playNotificationSound();
      }
      try {
        Vibration.vibrate([0, 200, 100, 200]);
      } catch {
        // Ignore vibration errors
      }
      return true;
    } else if (Platform.OS === 'web') {
      playWebChime();
      return true;
    } else {
      // iOS or other platforms: vibration fallback
      try {
        Vibration.vibrate(400);
      } catch {
        // Ignore
      }
      return true;
    }
  } catch (err) {
    console.warn('Error playing sound alert:', err);
    return false;
  }
};
